import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingBag, Zap, Truck, Shield, RotateCcw, Minus, Plus, Heart, Share2, Link as LinkIcon, Check, Clock, Bell } from "lucide-react";
import { toast } from "sonner";
import { fetchProduct, fetchReviews, submitReview, getSession, joinWaitlist, getSetting, fetchShippingRangeLabel } from "../lib/api";
import { useCart, formatINR } from "../contexts/CartContext";
import { useWishlist } from "../contexts/WishlistContext";
import ProductGallery from "../components/ProductGallery";
import SizeSelector from "../components/SizeSelector";
import Stars from "../components/Stars";
import Testimonials from "../components/Testimonials";
import SimilarProducts from "../components/SimilarProducts";
import { ChromeDivider } from "../components/Ticker";
import { useSEO } from "../hooks/useSEO";

export default function ProductDetail() {
  const { id } = useParams();
  const [shipCopy, setShipCopy] = useState("Shipping calculated by location at checkout");
  const [shipRange, setShipRange] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [copy, range] = await Promise.all([
        getSetting("shipping_copy_product", "Shipping calculated by location at checkout"),
        fetchShippingRangeLabel(),
      ]);
      if (cancelled) return;
      setShipCopy(copy);
      setShipRange(range);
    })();
    return () => { cancelled = true; };
  }, []);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [colorwayIdx, setColorwayIdx] = useState(0);
  const [colorwayPaused, setColorwayPaused] = useState(false);
  const { addItem, openCart } = useCart();
  const { isWishlisted, toggle } = useWishlist();

  useEffect(() => {
    setLoading(true);
    fetchProduct(id)
      .then((p) => {
        if (p) {
          setProduct(p);
          setSize(p.sizes?.[0] || "M");
        }
      })
      .catch(() => { /* swallow — fallback handles it */ })
      .finally(() => setLoading(false));
  }, [id]);

  useSEO({
    title: product ? `${product.name} | Million Threads` : "Million Threads",
    description: product?.seo_description || product?.description || "Premium streetwear. Limited drops. Designed for the future.",
    image: product?.images?.[0],
    structuredData: product && !product.coming_soon ? {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.name,
      description: product.seo_description || product.description,
      image: product.images || [],
      brand: { "@type": "Brand", name: "Million Threads" },
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        price: product.sale_price || product.price,
        availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      },
    } : null,
  });

  const colorways = product?.colorways || null;
  const hasColorways = Array.isArray(colorways) && colorways.length > 1;
  const activeColorway = hasColorways ? colorways[colorwayIdx] : null;
  const galleryImages = activeColorway ? activeColorway.images : product?.images;

  // Auto-rotate colorways every 4s (slow enough to read as intentional, not flicker),
  // until the user manually picks one. Skips while the tab is hidden.
  useEffect(() => {
    if (!hasColorways || colorwayPaused) return undefined;
    const t = setInterval(() => {
      if (document.hidden) return;
      setColorwayIdx((i) => (i + 1) % colorways.length);
    }, 4000);
    return () => clearInterval(t);
  }, [hasColorways, colorwayPaused, colorways]);

  // Close share menu on outside click
  useEffect(() => {
    if (!shareOpen) return undefined;
    const onClick = (e) => {
      if (!e.target.closest('[data-testid="pd-share-menu"]') && !e.target.closest('[data-testid="pd-share"]')) {
        setShareOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setShareOpen(false); };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [shareOpen]);

  const onAdd = () => {
    if (!product) return;
    const cwSuffix = activeColorway ? ` · ${activeColorway.label}` : "";
    const cwImage = activeColorway ? activeColorway.images[0] : product.image;
    for (let i = 0; i < qty; i++) {
      addItem({ ...product, size, image: cwImage, colorway: activeColorway ? activeColorway.label : null, name: product.name + cwSuffix });
    }
    toast.success(`${product.name}${cwSuffix} (${size}) added to cart.`, { duration: 1800 });
  };
  const onBuy = () => { onAdd(); setTimeout(() => openCart(), 200); };

  const onThreadedOut = async () => {
    if (!product) return;
    // Add to cart (so they can see it / it stays on their radar) but it cannot be paid for
    onAdd();
    // Log demand so we know who wants it back
    const session = await getSession();
    await joinWaitlist({ productId: product.id, productName: product.name, email: session?.user?.email });
    toast.success("Added. This drop is Threaded Out, so we'll note your interest and alert you when it's back.", { duration: 3000 });
  };

  const onWishlist = () => {
    if (!product) return;
    toggle(product);
    toast.success(isWishlisted(product.id) ? "Removed from wishlist" : "Saved to wishlist", { duration: 1400 });
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = product ? `Check out "${product.name}" on Million Threads, ${formatINR(product.price)}` : "";

  const onNativeShare = async () => {
    if (!product) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: product.name, text: shareText, url: shareUrl });
      } catch (e) { /* user cancelled */ }
    } else {
      setShareOpen((o) => !o);
    }
  };

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't copy");
    }
  };

  const shareTo = (platform) => {
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(shareText);
    const links = {
      whatsapp: `https://wa.me/?text=${t}%20${u}`,
      twitter: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${u}&description=${t}`,
      email: `mailto:?subject=${encodeURIComponent(product?.name || "")}&body=${t}%20${u}`,
    };
    window.open(links[platform], "_blank", "noopener,noreferrer");
    setShareOpen(false);
  };

  if (loading || !product) {
    return (
      <section className="mt-section">
        <div className="mt-container">
          <div className="mt-glass rounded-2xl h-[60vh] animate-pulse" />
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="page-first-section" data-testid="product-detail-section" style={{paddingBottom:"clamp(2.5rem,6vw,6rem)"}}>
        <div className="mt-container">
          {/* Breadcrumb */}
          <nav className="font-mono text-[0.62rem] tracking-mt uppercase opacity-60 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:opacity-100">Home</Link>
            <span className="mx-3">/</span>
            <Link to="/shop" className="hover:opacity-100">Shop</Link>
            <span className="mx-3">/</span>
            <span style={{ color: "var(--mt-text)" }}>{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:items-start">
            {/* Left: Gallery — capped so the product fits one screen instead of filling the full 7-col width */}
            <div className="lg:col-span-7 mt-enter lg:sticky lg:top-[88px]">
              <div className="mx-auto lg:mx-0 w-full max-w-[440px] lg:max-w-[min(100%,485px)]">
                <ProductGallery key={activeColorway ? activeColorway.id : "default"} productId={product.id} baseAngle={130} images={galleryImages} />
              </div>
            </div>

            {/* Right: Info — flows naturally, no internal scroll */}
            <div className="lg:col-span-5 mt-enter" style={{ animationDelay: "120ms" }}>
              <div className="mt-glass mt-rainbow-edge rounded-[22px] p-6 sm:p-8">
                {product.tag && (
                  <span className="inline-block font-mono text-[0.6rem] tracking-mt uppercase mb-3 lg:mb-3" style={{ color: product.tag.includes("LIMITED") ? "#C0A0FF" : "#00AAFF" }}>
                    {product.tag}{product.drop ? ` · ${product.drop}` : ""}
                  </span>
                )}
                <h1
  className="font-display font-extralight leading-[1.04] text-[clamp(2rem,4vw,3.2rem)]"
  style={{ color: "var(--mt-text)" }}
  data-testid="pd-name"
>
  {product.name}
</h1>
                <div className="mt-3 flex items-center gap-4 flex-wrap">
                  <span className="mt-pill" data-active={true}>{product.category}</span>
                  {product.review_count > 0 && <Stars value={product.rating} reviews={product.review_count} />}
                </div>

                <div className="mt-5 lg:mt-4">
                  <div className="flex items-baseline gap-3 flex-wrap" data-testid="pd-price">
                    {product.sale_price ? (<>
                      <span className="font-body font-bold text-3xl" style={{color:"#8B0000"}}>{formatINR(product.sale_price)}</span>
                      <span className="font-body text-xl line-through" style={{color:"var(--mt-text-muted)"}}>{formatINR(product.price)}</span>
                      <span className="font-mono text-[0.58rem] tracking-mt uppercase px-2 py-1 rounded-full" style={{background:"rgba(139,0,0,0.15)",color:"#8B0000"}}>Early Bird</span>
                    </>) : (
                      <span className="font-body font-bold text-3xl" style={{color:"var(--mt-text)"}}>{formatINR(product.price)}</span>
                    )}
                  </div>
                  <span className="mt-blood-line mt-3" aria-hidden="true" />
                </div>

                {hasColorways && (
                  <div className="mt-5 lg:mt-4" data-testid="colorway-selector">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-[0.62rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>
                        Colour: <span style={{ color: "var(--mt-text)" }}>{activeColorway.label}</span>
                      </span>
                      {!colorwayPaused && (
                        <span className="font-mono text-[0.52rem] tracking-mt uppercase opacity-40" style={{ color: "var(--mt-text-muted)" }}>
                          Auto-cycling
                        </span>
                      )}
                    </div>
                    <div className="flex gap-3">
                      {colorways.map((cw, i) => (
                        <button
                          key={cw.id}
                          type="button"
                          onClick={() => { setColorwayIdx(i); setColorwayPaused(true); }}
                          aria-label={cw.label}
                          aria-pressed={i === colorwayIdx}
                          className="relative rounded-full transition-transform hover:scale-110"
                          style={{
                            width: 34, height: 34, background: cw.swatch,
                            border: i === colorwayIdx ? "2px solid var(--mt-text)" : "2px solid var(--mt-glass-border)",
                            boxShadow: i === colorwayIdx ? "0 0 0 2px var(--mt-bg), 0 0 12px rgba(184,29,29,0.4)" : "none",
                          }}
                          data-testid={`colorway-${cw.id}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-5 lg:mt-4">
                  <SizeSelector
                    sizes={product.sizes}
                    outOfStock={product.sizes_out_of_stock || []}
                    value={size}
                    onChange={setSize}
                  />
                </div>

                {/* Qty + stock */}
                <div className="mt-5 lg:mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[0.65rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>Qty</span>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="mt-glass rounded-full w-8 h-8 flex items-center justify-center" data-testid="pd-qty-dec">
                        <Minus size={12} />
                      </button>
                      <span className="font-mono text-sm w-6 text-center" data-testid="pd-qty">{qty}</span>
                      <button type="button" onClick={() => setQty((q) => Math.min(product.stock, q + 1))} className="mt-glass rounded-full w-8 h-8 flex items-center justify-center" data-testid="pd-qty-inc">
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                  {product.stock > 0 && product.stock < 10 ? (
                    <p className="mt-blood font-mono text-[0.7rem] tracking-mt uppercase" data-testid="pd-stock">
                      Only {product.stock} left
                    </p>
                  ) : product.stock > 0 ? (
                    <p className="font-mono text-[0.7rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }} data-testid="pd-stock">
                      In stock
                    </p>
                  ) : (
                    <p className="font-mono text-[0.7rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }} data-testid="pd-stock">
                      Sold out
                    </p>
                  )}
                </div>

                <div className="mt-5 lg:mt-4 space-y-2.5">
                  {product.availabilityState === "sold_out" ? (
                    <>
                      <button type="button" onClick={onThreadedOut} className="mt-btn w-full" style={{ opacity: 0.85 }} data-testid="pd-threaded-out">
                        <Bell size={14} /> Threaded Out, Notify Me
                      </button>
                      <p className="font-mono text-[0.6rem] tracking-mt uppercase text-center" style={{ color: "var(--mt-text-muted)" }}>
                        This drop has sold out. Tap to be alerted when it returns.
                      </p>
                    </>
                  ) : product.availabilityState === "preorder" ? (
                    <>
                      <button type="button" onClick={onAdd} className="mt-btn mt-rainbow-edge w-full" data-testid="pd-add-to-cart">
                        <ShoppingBag size={14} /> Add to Cart
                      </button>
                      <button type="button" onClick={onBuy} className="mt-btn w-full mt-btn-blue" data-testid="pd-preorder">
                        <Clock size={14} /> Pre-Order
                      </button>
                      <p className="font-mono text-[0.6rem] tracking-mt uppercase text-center" style={{ color: "#00AAFF" }}>
                        Pre-order now · ships when the drop lands
                      </p>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={onAdd} className="mt-btn mt-rainbow-edge w-full" data-testid="pd-add-to-cart">
                        <ShoppingBag size={14} /> Add to Cart
                      </button>
                      <button type="button" onClick={onBuy} className="mt-btn w-full mt-btn-blue" data-testid="pd-buy-now">
                        <Zap size={14} /> Buy Now
                      </button>
                      {product.lowStock && (
                        <p className="font-mono text-[0.6rem] tracking-mt uppercase text-center mt-blood" style={{ fontWeight: 700 }} data-testid="pd-low-stock">
                          ◆ Few stitches left
                        </p>
                      )}
                    </>
                  )}

                  {/* Wishlist + Share row */}
                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={onWishlist}
                      className="mt-btn flex-1"
                      style={{
                        color: product && isWishlisted(product.id) ? "#FF4D6D" : "var(--mt-text)",
                        borderColor: product && isWishlisted(product.id) ? "rgba(255,77,109,0.45)" : "var(--mt-glass-border)",
                      }}
                      data-testid="pd-wishlist"
                    >
                      <Heart size={14} fill={product && isWishlisted(product.id) ? "#FF4D6D" : "transparent"} strokeWidth={1.8} />
                      {product && isWishlisted(product.id) ? "Saved" : "Wishlist"}
                    </button>

                    <div className="relative flex-1">
                      <button
                        type="button"
                        onClick={onNativeShare}
                        className="mt-btn w-full"
                        data-testid="pd-share"
                      >
                        <Share2 size={14} /> Share
                      </button>

                      {shareOpen && (
                        <div
                          className="mt-pop mt-keep-anim absolute right-0 top-[calc(100%+8px)] w-[260px] rounded-2xl p-2 z-50"
                          style={{
                            background: "var(--mt-modal-bg)",
                            border: "1px solid var(--mt-glass-border)",
                            boxShadow: "0 24px 60px -20px rgba(0,0,0,0.7)",
                            backdropFilter: "blur(34px) saturate(150%)",
                            WebkitBackdropFilter: "blur(34px) saturate(150%)",
                          }}
                          data-testid="pd-share-menu"
                        >
                          <p className="px-4 pt-2 pb-1 font-mono text-[0.55rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>Share to</p>
                          <button onClick={onCopyLink} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors" onMouseEnter={(e)=>e.currentTarget.style.background="var(--mt-glass-bg)"} onMouseLeave={(e)=>e.currentTarget.style.background="transparent"} style={{ color: "var(--mt-text)" }}>
                            {copied ? <Check size={14} style={{ color: "#00AAFF" }} /> : <LinkIcon size={14} style={{ color: "var(--mt-text-muted)" }} />}
                            <span className="font-body text-[0.82rem]">{copied ? "Copied!" : "Copy link"}</span>
                          </button>
                          <button onClick={() => shareTo("whatsapp")} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors" onMouseEnter={(e)=>e.currentTarget.style.background="var(--mt-glass-bg)"} onMouseLeave={(e)=>e.currentTarget.style.background="transparent"} style={{ color: "var(--mt-text)" }}>
                            <span className="font-body text-[0.82rem]">WhatsApp</span>
                          </button>
                          <button onClick={() => shareTo("twitter")} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors" onMouseEnter={(e)=>e.currentTarget.style.background="var(--mt-glass-bg)"} onMouseLeave={(e)=>e.currentTarget.style.background="transparent"} style={{ color: "var(--mt-text)" }}>
                            <span className="font-body text-[0.82rem]">X (Twitter)</span>
                          </button>
                          <button onClick={() => shareTo("facebook")} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors" onMouseEnter={(e)=>e.currentTarget.style.background="var(--mt-glass-bg)"} onMouseLeave={(e)=>e.currentTarget.style.background="transparent"} style={{ color: "var(--mt-text)" }}>
                            <span className="font-body text-[0.82rem]">Facebook</span>
                          </button>
                          <button onClick={() => shareTo("pinterest")} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors" onMouseEnter={(e)=>e.currentTarget.style.background="var(--mt-glass-bg)"} onMouseLeave={(e)=>e.currentTarget.style.background="transparent"} style={{ color: "var(--mt-text)" }}>
                            <span className="font-body text-[0.82rem]">Pinterest</span>
                          </button>
                          <button onClick={() => shareTo("email")} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors" onMouseEnter={(e)=>e.currentTarget.style.background="var(--mt-glass-bg)"} onMouseLeave={(e)=>e.currentTarget.style.background="transparent"} style={{ color: "var(--mt-text)" }}>
                            <span className="font-body text-[0.82rem]">Email</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <p className="mt-5 text-[0.7rem] font-mono tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>
                  {shipCopy}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About + Shipping */}
      <section className="mt-section pt-0" data-testid="pd-about">
        <div className="mt-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 relative mt-enter">
            <p aria-hidden="true" className="absolute -top-6 left-0 font-display italic font-light pointer-events-none whitespace-nowrap text-[clamp(2rem,5vw,4rem)] leading-none" style={{ opacity: 0.18, zIndex: 0 }}>
              Every thread has a story.
            </p>
            <div className="relative z-10 pt-16">
              <h2 className="font-display font-extralight leading-[1.08] text-[clamp(1.8rem,3.5vw,2.6rem)]">About This Piece</h2>
              <p className="mt-6 font-body text-base sm:text-lg leading-relaxed whitespace-pre-line" style={{ color: "var(--mt-text)" }}>
                {product.long_description || product.description || ""}
              </p>

              <div className="mt-8 flex flex-wrap gap-2" data-testid="pd-features">
                {(product.features || []).map((f) => (
                  <span key={f} className="mt-pill" data-active={true}>{f}</span>
                ))}
              </div>

              <h3 className="mt-12 font-display font-extralight text-[clamp(1.4rem,2.4vw,1.8rem)]">The Story Behind It</h3>
              <p className="mt-4 mt-italic-silver leading-relaxed text-base sm:text-lg max-w-xl">
                {product.story}
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 mt-enter" style={{ animationDelay: "140ms" }}>
            <div className="mt-glass mt-rainbow-edge rounded-[22px] p-7 sm:p-8 lg:sticky lg:top-28" data-testid="pd-shipping">
              <h3
  className="font-display font-extralight text-2xl"
  style={{ color: "var(--mt-text)" }}
>
  Shipping & Care
</h3>
              <div className="mt-6 space-y-4">
                <ShipRow icon={<Truck size={16} />} title="Shipping" sub={shipRange ? `${shipRange} by location, calculated at checkout` : "Calculated by location at checkout"} />
                <DividerLine />
                <ShipRow icon={<RotateCcw size={16} />} title="Easy Returns" sub="Unworn, with original tags" />
                <DividerLine />
                <ShipRow icon={<Shield size={16} />} title="Crafted to Last" sub="Designed in-house, tested extensively" />
              </div>

              <h4
  className="mt-8 font-mono text-[0.65rem] tracking-mt uppercase"
  style={{ color: "var(--mt-text)" }}
>
  Care
</h4>
              <ul className="mt-3 space-y-2 font-body text-sm">
                {(product.care || []).map((c, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="inline-block w-1 h-1 rounded-full bg-current opacity-40" aria-hidden="true" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <ProductReviews product={product} />
      <Testimonials />
      <ChromeDivider />
      <SimilarProducts category={product.category} excludeId={product.id} />
    </>
  );
}

function ShipRow({ icon, title, sub }) {
  return (
    <div className="flex items-start gap-4">
      <span className="mt-glass rounded-full p-2.5 flex-shrink-0" aria-hidden="true">{icon}</span>
      <div>
        <p
  className="font-body text-sm font-medium"
  style={{ color: "var(--mt-text)" }}
>
  {title}
</p>
        <p
  className="font-mono text-[0.6rem] tracking-mt uppercase"
  style={{ color: "var(--mt-text-muted)" }}
>
  {sub}
</p>
      </div>
    </div>
  );
}

function DividerLine() { return <div className="h-px" style={{ background: "linear-gradient(90deg, transparent, var(--mt-glass-border), transparent)" }} />; }


function StarBar({ rating, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="font-mono text-[0.6rem] tracking-mt w-2 text-right" style={{ color:"var(--mt-text-muted)" }}>{rating}</span>
      <span style={{ color:"#D4A457", fontSize:"0.65rem" }}>★</span>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background:"var(--mt-glass-border)" }}>
        <div className="h-full rounded-full transition-all duration-700" style={{ width:`${pct}%`, background:"linear-gradient(90deg, #D4A457, rgba(212,164,87,0.4))" }} />
      </div>
      <span className="font-mono text-[0.58rem] w-6 text-right" style={{ color:"var(--mt-text-muted)" }}>{count}</span>
    </div>
  );
}

function ProductReviews({ product }) {
  const [showAll, setShowAll] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [session, setSession] = useState(null);
  const [form, setForm] = useState({ rating: 0, body: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!product?.id) return;
    fetchReviews(product.id).then((r) => { setReviews(r); setLoading(false); }).catch(() => setLoading(false));
    getSession().then(setSession);
  }, [product?.id]);

  const submitForm = async (e) => {
    e.preventDefault();
    if (form.rating === 0 || !form.body.trim()) { toast.error("Add a rating and a few words"); return; }
    setBusy(true);
    try {
      await submitReview({ productId: product.id, rating: form.rating, body: form.body.trim() });
      const fresh = await fetchReviews(product.id);
      setReviews(fresh); setForm({ rating: 0, body: "" }); setShowForm(false);
      toast.success("Thanks for logging your wear.");
    } catch (err) { toast.error(err.message || "Could not submit review"); }
    finally { setBusy(false); }
  };

  const reviewCount = reviews.length;
  const hasReviews = reviewCount > 0;

  const ReviewForm = () => (
    <div className="mt-glass mt-rainbow-edge rounded-[22px] p-6 sm:p-8 mb-8 max-w-xl mx-auto">
      <h3 className="font-display text-xl font-light mb-5">Log Your Wear</h3>
      {!session?.user ? (
        <p className="mt-italic-silver">Please <a href="/profile" className="underline">sign in</a> to leave a review.</p>
      ) : (
        <form onSubmit={submitForm}>
          <div className="mb-4">
            <label className="font-mono text-[0.62rem] tracking-mt uppercase block mb-2" style={{ color: "var(--mt-text-muted)" }}>Rating</label>
            <div className="flex gap-1">
              {[1,2,3,4,5].map((n) => (
                <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} style={{ background: "none", border: "none", cursor: "pointer", padding: "2px" }} aria-label={`${n} stars`}>
                  <span style={{ color: form.rating >= n ? "#D4A457" : "var(--mt-glass-border)", fontSize: "1.4rem" }}>★</span>
                </button>
              ))}
            </div>
          </div>
          <div className="mb-4">
            <label className="font-mono text-[0.62rem] tracking-mt uppercase block mb-2" style={{ color: "var(--mt-text-muted)" }}>Your Words</label>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} placeholder="How does it wear? How does it feel?" className="mt-input" />
          </div>
          <button type="submit" disabled={busy} className="mt-btn mt-rainbow-edge w-full">{busy ? "..." : "Submit Review"}</button>
        </form>
      )}
    </div>
  );

  if (!hasReviews) {
    return (
      <section className="mt-section" data-testid="product-reviews">
        <div className="mt-container">
          {showForm && <ReviewForm />}
          <div className="mt-glass mt-rainbow-edge rounded-[22px] p-10 sm:p-14 text-center max-w-2xl mx-auto">
            <p className="font-mono text-[0.7rem] tracking-mt uppercase mb-5" style={{ color: "var(--mt-text-muted)" }}>Reviews</p>
            <h3 className="font-display font-extralight text-[clamp(1.6rem,3vw,2.4rem)]" style={{ lineHeight: 1.2, color: "var(--mt-text)" }}>
              No reviews yet. <span className="italic mt-chrome-text mt-keep-anim" style={{ fontWeight: 600 }}>Be the first.</span>
            </h3>
            <p className="mt-italic-silver mt-4 max-w-md mx-auto">Every thread starts with someone who moved first. Wear it, then tell the next person what it meant.</p>
            <button onClick={() => setShowForm(!showForm)} className="mt-btn mt-glass mt-6">{showForm ? "Cancel" : "✍ Log Your Wear"}</button>
          </div>
        </div>
      </section>
    );
  }

  const visible = showAll ? reviews : reviews.slice(0, 3);
  const avgRating = reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length;
  const starDist = [5, 4, 3, 2, 1].map((r) => ({ r, c: reviews.filter((rv) => Math.round(rv.rating) === r).length }));

  return (
    <section className="mt-section" data-testid="product-reviews">
      <div className="mt-container">
        {showForm && <ReviewForm />}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="font-mono text-[0.7rem] tracking-mt uppercase mb-6" style={{ color: "var(--mt-text-muted)" }}>Community Rating</p>
            <div className="mt-glass mt-rainbow-edge rounded-[22px] p-7">
              <div className="flex items-end gap-4 mb-6">
                <span className="font-display font-extralight" style={{ fontSize: "4.5rem", lineHeight: 1, color: "var(--mt-text)" }}>{avgRating.toFixed(1)}</span>
                <div className="pb-1">
                  <div className="flex gap-0.5 mb-1">
                    {[1,2,3,4,5].map((s2) => (<span key={s2} style={{ color: s2 <= Math.round(avgRating) ? "#D4A457" : "var(--mt-glass-border)", fontSize: "1rem" }}>★</span>))}
                  </div>
                  <p className="font-mono text-[0.62rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>{reviewCount} reviews</p>
                </div>
              </div>
              <div className="space-y-2">{starDist.map(({ r, c }) => <StarBar key={r} rating={r} count={c} total={reviewCount} />)}</div>
              <button onClick={() => setShowForm(!showForm)} className="mt-btn mt-glass w-full mt-6">{showForm ? "Cancel" : "✍ Log Your Wear"}</button>
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-6">
              <p className="font-mono text-[0.7rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>{reviewCount} Reviews</p>
            </div>
            <div className="space-y-4">
              {visible.map((r) => (
                <article key={r.id} className="mt-glass mt-rainbow-edge rounded-[20px] p-6" data-testid={`review-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-display text-base flex-shrink-0" style={{ background: "radial-gradient(circle at 35% 30%, #fff, rgba(192,160,255,0.4))", color: "#0a0a0a", border: "1px solid var(--mt-glass-border)" }}>{(r.author_name || "?")[0].toUpperCase()}</div>
                      <div>
                        <p className="font-display text-base font-light" style={{ color: "var(--mt-text)" }}>{r.author_name}</p>
                        <div className="flex gap-0.5 mt-0.5">{[1,2,3,4,5].map((s2) => (<span key={s2} style={{ color: s2 <= r.rating ? "#D4A457" : "var(--mt-glass-border)", fontSize: "0.7rem" }}>★</span>))}</div>
                      </div>
                    </div>
                    <p className="font-mono text-[0.55rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)", whiteSpace: "nowrap" }}>{(r.created_at || "").slice(0, 10)}</p>
                  </div>
                  <p className="font-body text-sm leading-relaxed mt-italic-silver">{r.body}</p>
                </article>
              ))}
            </div>
            {!showAll && reviews.length > 3 && (<button onClick={() => setShowAll(true)} className="mt-btn mt-rainbow-edge mt-6" data-testid="reviews-show-more">Show all {reviewCount} reviews</button>)}
          </div>
        </div>
      </div>
    </section>
  );
}
